/**
 * Editorial Video Section — Web Component
 *
 * Click-to-play with lazy loading. Nothing heavy loads until the visitor
 * clicks the play button:
 *  - Shopify-hosted video: a <video preload="none"> stored in a <template>
 *    is injected and played on demand.
 *  - YouTube / Vimeo: an iframe with autoplay is built from data attributes.
 */
class EditorialVideo extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.player = this.querySelector(".editorial-video__player");
    this.playBtn = this.querySelector(".editorial-video__play-btn");

    if (this.playBtn) {
      this.handlePlayClick = this.handlePlayClick.bind(this);
      this.playBtn.addEventListener("click", this.handlePlayClick);
    }
  }

  /** @param {Event} event */
  handlePlayClick(event) {
    event.preventDefault();
    this.startPlayback();
  }

  startPlayback() {
    if (!this.player || this.player.classList.contains("is-playing")) return;

    // 1) Shopify-hosted video stored in a <template>
    const template = /** @type {HTMLTemplateElement | null} */ (
      this.player.querySelector(".editorial-video__template")
    );
    if (template && template.content) {
      const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const video = fragment.querySelector("video");
      this.player.appendChild(fragment);
      this.player.classList.add("is-playing");

      if (video) {
        video.controls = true;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      }
      return;
    }

    // 2) External provider (YouTube / Vimeo)
    const videoType = this.player.getAttribute("data-video-type");
    const videoId = this.player.getAttribute("data-video-id");
    if (!videoType || !videoId) return;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("allow", "autoplay; encrypted-media; picture-in-picture; fullscreen");
    iframe.setAttribute("allowfullscreen", "");
    iframe.setAttribute("title", "Video player");

    if (videoType === "youtube") {
      iframe.src =
        "https://www.youtube.com/embed/" +
        encodeURIComponent(videoId) +
        "?autoplay=1&rel=0&playsinline=1";
    } else if (videoType === "vimeo") {
      iframe.src =
        "https://player.vimeo.com/video/" +
        encodeURIComponent(videoId) +
        "?autoplay=1";
    } else {
      return;
    }

    this.player.appendChild(iframe);
    this.player.classList.add("is-playing");
  }

  disconnectedCallback() {
    if (this.playBtn && this.handlePlayClick) {
      this.playBtn.removeEventListener("click", this.handlePlayClick);
    }
  }
}

if (!customElements.get("editorial-video")) {
  customElements.define("editorial-video", EditorialVideo);
}
